// Read-only scheduling suggestions based on capacity signals.
// No mutations or storage writes; UI-only recommendations.

import { addDays } from "./timeUtils.js";

const toDate = (value) => {
  const dt = value ? new Date(value) : null;
  return Number.isFinite(dt?.getTime?.()) ? dt : null;
};

function tasksOnDate(tasks = [], dateKey) {
  return (tasks || []).filter((t) => {
    const start = toDate(t.startDate || t.start);
    const end = toDate(t.dueDate || t.end || t.deadline || t.startDate);
    if (!start && !end) return false;
    const s = start || end;
    const e = end || start;
    const cursor = new Date(s);
    while (cursor <= e) {
      if (cursor.toISOString().slice(0, 10) === dateKey) return true;
      cursor.setDate(cursor.getDate() + 1);
    }
    return false;
  });
}

/**
 * Build UI-only scheduling suggestions to relieve capacity pressure.
 * @param {Object} input
 * @param {Array} input.tasks
 * @param {Object} input.capacity result from evaluateCapacity
 */
export function buildSchedulingSuggestions({ tasks = [], capacity = {} } = {}) {
  const suggestions = [];
  const dryingWarnings = capacity.dryingWarnings || [];
  const kilnWarnings = capacity.kilnWarnings || [];

  dryingWarnings.slice(0, 2).forEach((w, idx) => {
    const affected = tasksOnDate(tasks, w.date).filter((t) => (t.stage || t.status) === "drying");
    const sample = affected.slice(0, 2).map((t) => t.title || t.id).join(", ");
    const altDate = addDays(new Date(w.date), 1).toISOString().slice(0, 10);
    suggestions.push({
      id: `suggest-drying-${w.date}-${idx}`,
      title: "Drying overload",
      message: `Move some drying tasks from ${w.date} to ${altDate}. E.g., ${sample || "selected tasks"}.`,
    });
  });

  kilnWarnings.slice(0, 2).forEach((w, idx) => {
    const affected = tasksOnDate(tasks, w.date).filter((t) =>
      ["bisque", "glazeFiring", "glaze", "kiln"].includes(t.stage || t.status || "")
    );
    const sample = affected.slice(0, 2).map((t) => t.title || t.id).join(", ");
    const altDate = addDays(new Date(w.date), 1).toISOString().slice(0, 10);
    suggestions.push({
      id: `suggest-kiln-${w.date}-${idx}`,
      title: "Kiln load high",
      message: `Stagger firings around ${w.date}. Consider shifting ${sample || "some loads"} to ${altDate}.`,
    });
  });

  return suggestions;
}
